/**
 * Todos selectors — memoized via reselect.
 * Selectors operate on the todos slice of state, or the full RootState.
 *
 * selectAllTodos and selectFilteredTodos use createSelector for memoization.
 * The reducer always creates a new byId reference when any todo is updated,
 * so the memoized selectors correctly recompute when todo properties change
 * (including close_reason). This avoids the stale-cache issue while also
 * preventing the excessive re-render cascade that was causing edit mode to
 * close on every field change (task-39ee9db3).
 */

import { createSelectorCreator, lruMemoize } from 'reselect';
import { Todo } from '@/infra/types/domain';
import { SmartTodoResult } from '@/infra/types/api';
import { TodosState, TodoFilters } from './ducks';
import {
  defaultHostFrame,
  evalOrder,
  compareElkaValues,
  buildTodoContext,
  walker,
} from '@/features/predicate/walker';
import { runFilterRule } from '@/features/predicate/selectors';
import { latestRules } from '@/features/predicate/rule-cache';
import { AppDefaults } from '@/infra/wasm/AppDefaults';
import { pluginRegistry } from '@/infra/plugin-registry';

/**
 * Custom createSelector that always re-evaluates input selectors.
 *
 * Reselect v5's default argsMemoize (weakMapMemoize) caches by top-level
 * argument identity — if the same state object reference is passed, it
 * returns the cached result without checking whether input selector results
 * have changed. This breaks when state properties are mutated in place
 * (as some tests do) while the parent reference stays the same.
 *
 * By using a pass-through argsMemoize, input selectors always run.
 * The result function is still memoized via lruMemoize on input selector
 * output equality, so unchanged inputs return the cached array reference
 * (preventing the re-render cascade that caused edit mode to close,
 * task-39ee9db3).
 */
const createAppSelector = createSelectorCreator({
  memoize: lruMemoize,
  argsMemoize: (fn: (...args: unknown[]) => unknown) => fn,
});

// ─── Input Selectors ───────────────────────────────────────

// Accept either { todos: TodosState } (RootState) or TodosState directly
type StateWithTodos = { todos: TodosState } | TodosState;

function getTodosState(state: StateWithTodos): TodosState {
  return 'todos' in state && 'byId' in (state as any).todos
    ? (state as { todos: TodosState }).todos
    : (state as TodosState);
}

const getById = (state: StateWithTodos) => getTodosState(state).byId;
const getAllIds = (state: StateWithTodos) => getTodosState(state).allIds;
const getFilters = (state: StateWithTodos) => getTodosState(state).filters;

// Resolve the user's day_start_hour from the auth slice when available
// (selectors are called with either the full RootState or just the
// todos slice). Fallback flows through AppDefaults so the wasm-sourced
// snapshot stays the single source of truth across the web client and
// matches Go's `core.DayStartHourFallback`.
const getDayStartHour = (state: StateWithTodos): number => {
  const auth = (state as { auth?: { user?: { day_start_hour?: number } } }).auth;
  return auth?.user?.day_start_hour ?? AppDefaults.dayStartHourFallback;
};

// ─── Derived Selectors ─────────────────────────────────────

/**
 * Select all todos as an array (preserving order from allIds).
 * Memoized via createSelector: recomputes only when byId or allIds
 * references change. The reducer always creates new byId/allIds
 * references on update, so close_reason changes are reflected.
 * Prevents the re-render cascade that caused edit mode to close
 * on every field change (task-39ee9db3).
 */
export const selectAllTodos = createAppSelector(
  [getById, getAllIds],
  (byId, allIds): Todo[] => allIds.map((id) => byId[id]).filter(Boolean),
);

/**
 * Select todos visible for today, sorted by type then chronological order.
 * - No due_at: always visible, sorted first
 * - Overdue (past day): always visible
 * - Today: visible after `user.day_start_hour` local time
 * - Future: hidden
 * Sort: no-due-date → date-only → timed (chronological)
 */
export const selectVisibleSortedTodos = createAppSelector(
  [getById, getAllIds, getDayStartHour],
  (byId, allIds, dayStartHour): Todo[] => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart.getTime() + 86400000);
    const dayStart = new Date(todayStart.getTime() + dayStartHour * 3600000);

    const todos = allIds.map((id) => byId[id]).filter(Boolean);

    const visible = todos.filter((todo) => {
      if (!todo.due_at) return true;
      const dueDay = new Date(todo.due_at);
      const dueDayStart = new Date(dueDay.getFullYear(), dueDay.getMonth(), dueDay.getDate());
      if (dueDayStart < todayStart) return true;
      if (dueDayStart >= tomorrowStart) return false;
      return now >= dayStart;
    });

    return [...visible].sort((a, b) => {
      if (!a.due_at && !b.due_at) return 0;
      if (!a.due_at) return -1;
      if (!b.due_at) return -1;

      const aDateOnly = a.due_date_only ?? false;
      const bDateOnly = b.due_date_only ?? false;
      if (aDateOnly && !bDateOnly) return -1;
      if (!aDateOnly && bDateOnly) return 1;

      return new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
    });
  },
);

// Natural keys for each section's default ordering. Used both as the fallback
// sort comparator when no sort_order is set, AND as the "effective sort value"
// of un-reordered items so drag midpoints can position between a reordered and
// a non-reordered item. See effectiveSort() below.
function todayNaturalKey(t: Todo): number {
  const ms = new Date(t.due_at!).getTime();
  // Nudge date-only before same-day timed items (the prior explicit rule).
  return (t.due_date_only ?? false) ? ms - 1 : ms;
}

function inboxNaturalKey(t: Todo): number {
  // The Inbox "natural" (unpinned) position is whatever rule.order.todo.inbox
  // computes for this row with sort_order nulled — i.e. its priority band +
  // recency key. Deriving it from the rule (rather than replicating the band
  // geometry here) keeps the single source of truth in the seed rule and keeps
  // drag midpoints on exactly the scale the display order uses. effectiveSort()
  // then reproduces the rule's primary value for every row (pinned → sort_order,
  // unpinned → this key), so a dragged midpoint lands where the list shows it.
  const primary = latestRules()?.get('rule.order.todo.inbox')?.order?.primary;
  if (primary) {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const host = defaultHostFrame('', tz, []);
      const ctx = buildTodoContext({ ...t, sort_order: null } as unknown as Todo, host);
      const v = walker.eval(primary, ctx);
      if (v.type === 'AST_TYPE_DOUBLE') return v.value as number;
    } catch {
      // Fall through to the cold-start key below.
    }
  }
  // Cold start (rule snapshot not yet synced): newest-first by created_at.
  // Negate so a lower effective value sorts earlier.
  return -new Date(t.created_at).getTime();
}

function upcomingNaturalKey(t: Todo): number {
  return new Date(t.due_at!).getTime();
}

function effectiveSort(t: Todo, natural: (t: Todo) => number): number {
  return t.sort_order != null ? t.sort_order : natural(t);
}

// orderByRule walks a seeded OrderRule (e.g. rule.order.todo.time_asc)
// over each row, caches the (primary, tiebreaker) Value pair, then
// sorts the row set by elka's natural Value compare. Falls back to
// the supplied imperative comparator when the rule isn't in the
// snapshot yet (cold-start) or a per-row walker eval traps.
function orderByRule(
  items: Todo[],
  ruleId: string,
  fallbackCompare: (a: Todo, b: Todo) => number,
): Todo[] {
  const rules = latestRules();
  const rule = rules?.get(ruleId);
  const order = rule?.order;
  if (!order) {
    return [...items].sort(fallbackCompare);
  }
  const primaryDesc = order.primaryDescending === true;
  const tiebreakerDesc = order.tiebreakerDescending === true;
  // Build host once; per-row context is cheap.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const host = defaultHostFrame('', tz, []);
  const keyed = items.map((t) => {
    const ctx = buildTodoContext(t, host);
    const pair = evalOrder(order.primary, order.tiebreaker, ctx);
    return { t, pair };
  });
  keyed.sort((a, b) => {
    if (!a.pair && !b.pair) return fallbackCompare(a.t, b.t);
    if (!a.pair) return 1;
    if (!b.pair) return -1;
    const p = compareElkaValues(a.pair.primary, b.pair.primary);
    if (p !== 0) return primaryDesc ? -p : p;
    if (a.pair.tiebreaker && b.pair.tiebreaker) {
      const t = compareElkaValues(a.pair.tiebreaker, b.pair.tiebreaker);
      return tiebreakerDesc ? -t : t;
    }
    return 0;
  });
  return keyed.map((k) => k.t);
}

/**
 * Select "Today" todos: pending/in-progress with due_at before the
 * user's logical day_end. Includes overdue. Excludes undated rows
 * (Inbox) and rows past day_end (Upcoming). Bucket membership is
 * defined by elka's filter.today rule.
 */
export const selectTodayTodos = createAppSelector(
  [getById, getAllIds, getDayStartHour],
  (byId, allIds, dayStartHour): Todo[] => {
    const todos = allIds.map((id) => byId[id]).filter(Boolean);
    const visible = runWalkerFilter('filter.today', todos, dayStartHour);

    // Time-anchored section: elka's rule.order.todo.time_asc owns
    // the ordering — primary = todo.due_at, tiebreaker = todo.title.
    // sort_order is intentionally ignored here (manual reorder intent
    // belongs to Inbox). Imperative fallback covers cold-start before
    // the rule snapshot arrives.
    return orderByRule(visible, 'rule.order.todo.time_asc', (a, b) => {
      const dt = todayNaturalKey(a) - todayNaturalKey(b);
      if (dt !== 0) return dt;
      return a.title.localeCompare(b.title);
    });
  },
);

/**
 * Select "Inbox" todos: pending/in-progress with no due_at. Sort_order-aware;
 * falls back to created_at DESC when not manually reordered.
 */
export const selectInboxTodos = createAppSelector(
  [getById, getAllIds, getDayStartHour],
  (byId, allIds, dayStartHour): Todo[] => {
    const todos = allIds.map((id) => byId[id]).filter(Boolean);
    const matching = runWalkerFilter('filter.inbox', todos, dayStartHour);
    // Drop todos a plugin claims for its own inbox surface (e.g. Projects'
    // "Show project tasks" section). Keeps project knowledge out of the core
    // filter.inbox rule — the exclusion lives in the projects plugin and only
    // applies while it's enabled. See plugin-registry claimsInboxTodo.
    const owned = matching.filter((t) => !pluginRegistry.claimsInboxTodo(t));
    return orderByRule(owned, 'rule.order.todo.inbox', (a, b) =>
      effectiveSort(a, inboxNaturalKey) - effectiveSort(b, inboxNaturalKey),
    );
  },
);

/**
 * Select "Completed today" todos: done with completed_at inside the
 * user's logical-day window. Bucket membership is defined by elka's
 * filter.completed_today rule. For recurring series, keeps only the
 * most recently completed instance.
 */
export const selectTodayCompleted = createAppSelector(
  [getById, getAllIds, getDayStartHour],
  (byId, allIds, dayStartHour): Todo[] => {
    const todos = allIds.map((id) => byId[id]).filter(Boolean);
    const completedToday = runWalkerFilter('filter.completed_today', todos, dayStartHour);

    // Dedup recurring: group by title+owner+recurrence_rule, keep latest completed_at
    const seen = new Map<string, Todo>();
    for (const t of completedToday) {
      if (t.recurrence_rule) {
        const key = `${t.title}|${t.owner_id}|${t.recurrence_rule}`;
        const existing = seen.get(key);
        if (!existing || new Date(t.completed_at!).getTime() > new Date(existing.completed_at!).getTime()) {
          seen.set(key, t);
        }
      } else {
        seen.set(t.id, t);
      }
    }
    return orderByRule(Array.from(seen.values()), 'rule.order.todo.completed_desc', (a, b) =>
      new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime()
    );
  },
);

/**
 * Select "Upcoming" todos: pending/in-progress with due_at in the future (tomorrow+).
 * Exact-duplicate rows are collapsed via dedupExact so two daily reminders
 * at different times of day stay separate.
 */
export const selectUpcomingTodos = createAppSelector(
  [getById, getAllIds, getDayStartHour],
  (byId, allIds, dayStartHour): Todo[] => {
    const todos = allIds.map((id) => byId[id]).filter(Boolean);
    const upcoming = runWalkerFilter('filter.upcoming', todos, dayStartHour);
    return orderByRule(dedupExact(upcoming), 'rule.order.todo.time_asc', (a, b) => {
      const dt = upcomingNaturalKey(a) - upcomingNaturalKey(b);
      if (dt !== 0) return dt;
      return a.title.localeCompare(b.title);
    });
  },
);

/**
 * UpcomingGroup is one section in the Upcoming view: Tomorrow /
 * This week / Next week / Later. Bucket membership is driven by
 * the elka filter.upcoming.<bucket> rules so iOS and web partition
 * the same dated rows the same way.
 */
export interface UpcomingGroup {
  bucket: 'tomorrow' | 'this_week' | 'next_week' | 'later';
  title: string;
  items: Todo[];
}

const UPCOMING_BUCKETS: Array<{
  bucket: UpcomingGroup['bucket'];
  ruleID: string;
  title: string;
}> = [
  { bucket: 'tomorrow', ruleID: 'filter.upcoming.tomorrow', title: 'Tomorrow' },
  { bucket: 'this_week', ruleID: 'filter.upcoming.this_week', title: 'This week' },
  { bucket: 'next_week', ruleID: 'filter.upcoming.next_week', title: 'Next week' },
  { bucket: 'later', ruleID: 'filter.upcoming.later', title: 'Later' },
];

/**
 * Sectioned Upcoming. Walker is the only source of truth — returns
 * empty groups until the rule cache hydrates. Exact-duplicate rows
 * are collapsed inside each bucket via dedupExact.
 */
export const selectUpcomingGroups = createAppSelector(
  [getById, getAllIds, getDayStartHour],
  (byId, allIds, dayStartHour): UpcomingGroup[] => {
    const todos = allIds.map((id) => byId[id]).filter(Boolean);
    const rules = latestRules();
    if (!rules || rules.size === 0) return [];
    const host = defaultHostFrame('', '', [], '', 10, dayStartHour);
    const groups: UpcomingGroup[] = [];
    for (const b of UPCOMING_BUCKETS) {
      let matching: Todo[];
      try {
        matching = runFilterRule(rules, b.ruleID, todos, host);
      } catch {
        matching = [];
      }
      if (matching.length === 0) continue;
      const deduped = dedupExact(matching);
      const sorted = orderByRule(deduped, 'rule.order.todo.time_asc', (a, b2) => {
        const dt = upcomingNaturalKey(a) - upcomingNaturalKey(b2);
        if (dt !== 0) return dt;
        return a.title.localeCompare(b2.title);
      });
      groups.push({ bucket: b.bucket, title: b.title, items: sorted });
    }
    return groups;
  },
);

// Fields that change between two rows representing the same intent —
// row identity, server housekeeping, and bookkeeping the user never
// authored. Two todos that match on every OTHER field are treated as
// duplicates. Anything semantic (title, owner, due_at, recurrence,
// description, priority, project, visibility, notify config,
// vanish config, context, smart-input source) participates in the
// key so legitimately distinct rows — e.g. two daily reminders at
// different times of day — stay separate.
const DUP_IGNORED_FIELDS = new Set<string>([
  'id',
  'created_at',
  'updated_at',
  'deleted_at',
  'sort_order',
  'enhance_workflow_id',
  'enhancement_status',
  'project_title',
]);

function dedupKey(t: Todo): string {
  const keys = Object.keys(t).filter((k) => !DUP_IGNORED_FIELDS.has(k)).sort();
  const projection: Record<string, unknown> = {};
  for (const k of keys) {
    projection[k] = (t as unknown as Record<string, unknown>)[k];
  }
  return JSON.stringify(projection);
}

function dedupExact(rows: Todo[]): Todo[] {
  const seen = new Map<string, Todo>();
  for (const t of rows) {
    const key = dedupKey(t);
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, t);
      continue;
    }
    // Tie-break to the server's latest view so subsequent edits to
    // the surviving row land on the freshest copy. Falls through to
    // first-seen when updated_at is absent on either side.
    const a = existing.updated_at ?? '';
    const b = t.updated_at ?? '';
    if (b > a) seen.set(key, t);
  }
  return Array.from(seen.values());
}

// Export for drag-drop handlers that need to compute midpoints between neighbors.
export { todayNaturalKey, inboxNaturalKey, upcomingNaturalKey, effectiveSort };

// runWalkerFilter is the single source of truth for the four todo
// filter rules (filter.today / filter.inbox / filter.upcoming /
// filter.completed_today). Returns [] before the rule cache hydrates
// or when the walker throws — there is no imperative fallback.
// Removing the fallback eliminates the rule-vs-TS divergence risk
// flagged in Plan §9 Phase 4 (C3); cold-start briefly shows empty
// sections instead of a stale-shape parallel implementation.
function runWalkerFilter(ruleID: string, todos: Todo[], dayStartHour: number): Todo[] {
  const rules = latestRules();
  if (!rules || rules.size === 0) return [];
  const host = defaultHostFrame('', '', [], '', 10, dayStartHour);
  try {
    return runFilterRule(rules, ruleID, todos, host);
  } catch {
    return [];
  }
}

/**
 * Select todos filtered by current filter state.
 * Filters use AND logic — all active filters must match.
 * Memoized: recomputes only when the todo list or filters change.
 */
export const selectFilteredTodos = createAppSelector(
  [selectAllTodos, getFilters],
  (todos, filters): Todo[] =>
    todos.filter((todo) => {
      if (filters.status !== 'all' && todo.status !== filters.status) return false;
      if (filters.owner !== 'all' && todo.owner_id !== filters.owner) return false;
      if (filters.source !== 'all' && todo.source_plugin !== filters.source) return false;
      if (filters.close_reason !== 'all' && todo.close_reason !== filters.close_reason) return false;
      return true;
    }),
);

/**
 * Select todos that carry a given tag, matched case-insensitively against
 * the trimmed tag name. Memoized: recomputes only when the todo list or the
 * requested tag changes. The tag arg is normalized so callers can pass any
 * casing (e.g. 'home-maintenance' matches a 'Home-Maintenance' tag).
 */
export const selectTodosByTag = createAppSelector(
  [selectAllTodos, (_state: StateWithTodos, tagName: string) => tagName],
  (todos, tagName): Todo[] => {
    const key = tagName.trim().toLowerCase();
    if (!key) return [];
    return todos.filter((todo) =>
      (todo.tags ?? []).some((t) => t.trim().toLowerCase() === key),
    );
  },
);

/**
 * Group todos by status.
 */
export const selectTodosByStatus = createAppSelector(
  [selectAllTodos],
  (todos): Record<string, Todo[]> => {
    const grouped: Record<string, Todo[]> = {};
    for (const todo of todos) {
      if (!grouped[todo.status]) {
        grouped[todo.status] = [];
      }
      grouped[todo.status].push(todo);
    }
    return grouped;
  }
);

/**
 * Select a single todo by ID.
 */
export const selectTodoById = (state: StateWithTodos, id: string): Todo | undefined => {
  return getTodosState(state).byId[id];
};

/**
 * Select loading state.
 */
export const selectTodosLoading = (state: StateWithTodos): boolean => {
  return getTodosState(state).loading;
};

/**
 * Select error state.
 */
export const selectTodosError = (state: StateWithTodos): string | null => {
  return getTodosState(state).error;
};

/**
 * Select current filters.
 */
export const selectTodoFilters = (state: StateWithTodos): TodoFilters => {
  return getTodosState(state).filters;
};

/**
 * Select smart-create loading state.
 */
export const selectSmartLoading = (state: StateWithTodos): boolean => {
  return getTodosState(state).smartLoading;
};

/**
 * Select smart-create error state.
 */
export const selectSmartError = (state: StateWithTodos): string | null => {
  return getTodosState(state).smartError;
};

/**
 * Select the last todo created via smart-create.
 */
export const selectLastSmartTodo = (state: StateWithTodos): Todo | null => {
  return getTodosState(state).lastSmartTodo;
};

/**
 * Select the last smart todo result (includes action type for non-create intents).
 */
export const selectLastSmartResult = (state: StateWithTodos): SmartTodoResult | null => {
  return getTodosState(state).lastSmartResult;
};
